// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-nocheck

import './_library-chart.scss';
import {Chart} from 'chart.js/auto';
import {FormatModule, Tabulator} from 'tabulator-tables';
import {approximateArray, replaceTimestamp} from '@/ts/utilities';
import {
    AV_LANGUAGES,
    DEFAULT_COUNTRY,
    DEFAULT_N_OF_PERIOD,
    DEFAULT_PERIOD,
    PERIOD_FILTERS,
    SPINNER_DURATION
} from '@/ts/enums';
import {resolve} from '@/index';
import {SearchableSelect} from "@/ts/utils/custom-select-full";

// Init DataView component and handle select options

export default function DataView() {
    const $dataView = document.querySelectorAll('.data-view');

    if ($dataView.length) {
        $dataView.forEach($chart => {
            const $tabs = $chart.querySelectorAll('.tab');
            const $tabsContent = $chart.querySelectorAll('.tab-content');
            const $number = $chart.querySelector('.number') as HTMLDivElement;
            const $initialNumberLabel = $number.innerHTML;
            const $lastUpdateDate = $chart.querySelector('.date.last-update') as HTMLDivElement;
            const $tooltipLabel = $chart.querySelector('.tooltip-label') as HTMLDivElement;
            const htmlLang = document.querySelector('html') ? document.querySelector('html').getAttribute('lang') : '';
            const currentLanguage: string = htmlLang && AV_LANGUAGES.find(lang => lang.includes(htmlLang)) ? AV_LANGUAGES.find(lang => lang.includes(htmlLang)) : 'en-GB';
            const $othersLabel = $chart.querySelector('.others-label') as HTMLDivElement;
            let $filtersSelect = $chart.querySelectorAll('.select-wrapper .select-container');
            let initialDefaultPeriod = DEFAULT_PERIOD;
            let initialDefaultNumberOfPeriod = DEFAULT_N_OF_PERIOD;
            let initialDefaultCountry = DEFAULT_COUNTRY;
            const $noResult = $chart.querySelector('.no-result');
            const $tabContents = $chart.querySelector('.tab-contents');
            const initialYearText = $chart.querySelector('.year').innerHTML;

            Tabulator.registerModule([FormatModule]);

            replaceTimestamp();

            let chart: any;
            let timestamp: any;
            const index = 0;
            let limitLength = 8;
            const colors = ['#006EDB', '#D15000', '#99C7F5', '#F07E37', '#1B558E', '#F0B590'];

            let data: any;
            let dataTable: any;
            const chartType = $chart.getAttribute('data-type');
            const chartApi = $chart.getAttribute('data-api-dataview');

            bindChart(initialDefaultPeriod, initialDefaultNumberOfPeriod, initialDefaultCountry);

            handleTabs();

            bindInitialSelect();

            function handleTabs() {
                if (chartType === 'doughnut') {
                    const $tabsWrapper = $chart.querySelector('.tabs-wrapper') as HTMLDivElement;
                    const $infoWrapper = $chart.querySelector('.info-wrapper') as HTMLDivElement;
                    $infoWrapper.classList.add('d-none');
                    $tabsWrapper.classList.add('d-none');
                    $number.classList.add('d-none');
                }

                if ($tabs.length > 0) {
                    const $tabTable = $chart.querySelector('.tab[data-id="table"]') as HTMLElement;
                    $tabs.forEach(($tab, index) => {
                        if (index === 0) {
                            $tab.classList.add('selected');

                            $tabsContent.forEach($content => {
                                if ($tab.getAttribute('data-id') === $content.getAttribute('id')) {
                                    $content.classList.add('selected');
                                } else {
                                    $content.classList.remove('selected');
                                }
                            });
                        }

                        $tab.addEventListener('click', () => {
                            $tabs.forEach($el => {
                                $el.classList.remove('selected');
                            });
                            $tab.classList.add('selected');

                            $tabsContent.forEach($content => {
                                if ($tab.getAttribute('data-id') === $content.getAttribute('id')) {
                                    $content.classList.add('selected');

                                    if ($tabTable.classList.contains('selected')) {
                                        ($chart.querySelector('.chart-table-wrapper') as HTMLElement).classList.add('has-table');
                                    } else {
                                        ($chart.querySelector('.chart-table-wrapper') as HTMLElement).classList.remove('has-table');
                                    }
                                } else {
                                    $content.classList.remove('selected');
                                }
                            });

                            if ($tab.hasAttribute('data-id') && $tab.getAttribute('data-id') === 'table' && initialDefaultCountry && initialDefaultCountry !== '*') {
                                initialDefaultCountry = '';
                                bindChart(initialDefaultPeriod, initialDefaultNumberOfPeriod, '');
                            }
                        });

                        $tab.addEventListener('keypress', (e: KeyboardEvent) => {
                            if (e.key === 'Enter') {
                                $tabs.forEach($el => {
                                    $el.classList.remove('selected');
                                });
                                $tab.classList.add('selected');

                                $tabsContent.forEach($content => {
                                    if ($tab.getAttribute('data-id') === $content.getAttribute('id')) {
                                        $content.classList.add('selected');
                                    } else {
                                        $content.classList.remove('selected');
                                    }
                                });
                            }
                        });
                    });
                }
            }

            function getMonthName(dataString) {
                const date = new Date(dataString);
                let monthName = date.toLocaleDateString(currentLanguage, {month: 'short'});
                monthName = monthName.charAt(0).toUpperCase() + monthName.slice(1);

                return monthName;
            }

            function getDay(dataString: any) {
                const date = new Date(dataString);
                return date.toLocaleDateString(currentLanguage, {day: 'numeric', month: 'short'});
            }

            function create(data: any, index: number) {
                let lineData: any;
                if (chartType === 'line') {
                    if (data[0] && data[0].label && data[0].label.split(',') && data[0].label.split(',').length === 2) {
                        lineData = data.map(d => {
                            const weeks = d.label.split(',');
                            let label = weeks.map((d: any) => getDay(d));

                            return label.join(' - ');
                        });
                    } else {
                        if (data[0] && data[0].label && data[0].label.split('-') && data[0].label.split('-').length === 3) {
                            lineData = data.map((item: any) => getDay(item.label));
                        } else {
                            lineData = data.map((item: any) => getMonthName(item.label));
                        }
                    }
                }
                const labels = chartType === 'doughnut' ? data.map(item => item.label) : lineData;
                const values = data.map(item => item.value);
                const percentage = data.map(item => item.percentage);

                const datasets = [
                    {
                        label: chartType === labels,
                        data: chartType === 'doughnut' ? percentage : values,
                        ...addExtras(chartType, index),
                    },
                ];

                const date = new Date(timestamp);
                const formattedDate = date.toLocaleDateString(currentLanguage, {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                });

                if ($lastUpdateDate) {
                    $lastUpdateDate.textContent = formattedDate;
                }

                init(chartType, labels, datasets, initOptions(chartType));
            }

            function init(chartType: string, labels: any, data: any, options: any) {
                let ctx = $chart.querySelector('canvas');
                if (ctx) {
                    const existingChart = Chart.getChart(ctx);

                    if (existingChart) {
                        existingChart.destroy();
                    }

                    $chart.classList.add(`${chartType}`);
                    ctx.classList.add(`${chartType}`);
                    ctx.setAttribute('id', `chart-${index}`);
                    let chartConfig = {
                        type: chartType,
                        data: {
                            labels: labels,
                            datasets: data,
                        },
                        options: {
                            ...options,
                            responsive: true,
                            maintainAspectRatio: true,
                        },
                    };

                    chart = new Chart(ctx, chartConfig);
                }
            }

            function addExtras(chartType: string, colorIndex: number) {
                let extras = {};

                switch (chartType) {
                    case 'line':
                        extras = {
                            borderColor: colors[colorIndex % colors.length],
                            pointBackgroundColor: colors[colorIndex % colors.length],
                        };
                        break;
                    case 'doughnut':
                        extras = {
                            backgroundColor: colors,
                            hoverOffset: 8,
                        };
                        break;

                    default:
                        break;
                }

                return extras;
            }

            function initOptions(chartType: string) {
                const $selectOpener = $chart.querySelector('.select-container[name="country"] .search-select__opener') as HTMLElement;
                let chartOptions = {};

                switch (chartType) {
                    case 'line':
                        chartOptions = {
                            scales: {
                                y: {
                                    beginAtZero: true,
                                },
                            },
                            plugins: {
                                legend: {
                                    display: false,
                                },
                                tooltip: {
                                    enabled: true,
                                    backgroundColor: '#FFF',
                                    bodyColor: '#302C39',
                                    titleColor: '#302C39',
                                    footerColor: '#302C39',
                                    borderColor: '#2F475E',
                                    borderWidth: 1,
                                    padding: 16,
                                    cornerRadius: 2,
                                    displayColors: false,
                                    titleFont: {
                                        size: 16,
                                        weight: 'normal',
                                        family: 'Titillium Web',
                                    },
                                    bodyFont: {
                                        size: 32,
                                        weight: 'bold',
                                        family: 'Roboto Mono',
                                    },
                                    titleFooter: {
                                        size: 14,
                                        family: 'Titillium Web',
                                    },
                                    callbacks: {
                                        title: () => $selectOpener.innerText.trim(),
                                        label: context => context.formattedValue,
                                        footer: () => $tooltipLabel.innerText.trim(),
                                    },
                                },
                            },
                            elements: {
                                point: {
                                    radius: 6,
                                    hoverRadius: 6,
                                },
                            },
                        };
                        break;
                    case 'doughnut':
                        chartOptions = {
                            onHover: (event: any, chartElement: any) => {
                                if (chartElement && chartElement[0]) {
                                    event.native.target.style.cursor = chartElement[0] ? 'pointer' : 'default';
                                    const index = chartElement[0].index;
                                    const legendItems = $chart.querySelectorAll('.legend-item');

                                    legendItems.forEach((item: any) => {
                                        item.classList.remove('underline');
                                    });

                                    if (legendItems[index]) {
                                        legendItems[index].classList.add('underline');
                                    }
                                } else {
                                    const legendItems = $chart.querySelectorAll('.legend-item');
                                    legendItems.forEach((item: any) => {
                                        item.classList.remove('underline');
                                    });
                                }
                            },
                            spacing: 3,
                            plugins: {
                                legend: {
                                    display: false,
                                },
                                tooltip: {
                                    enabled: false,
                                },
                            },
                        };
                        break;

                    default:
                        break;
                }

                return chartOptions;
            }

            function createTable(data, totalData) {
                const $table = $chart.querySelector('.table') as HTMLElement;
                const $tableColumn = $chart.querySelector('.table-column-label') as HTMLDivElement;
                const $tooltipLabel = $chart.querySelector('.tooltip-label') as HTMLDivElement;
                const $totalCountriesLabel = $chart.querySelector('.total-countries-label') as HTMLDivElement;
                const $tableColumnLabel = $tableColumn.innerText.trim();
                let monthName: any;

                const labels =
                    totalData &&
                    totalData.map((item: any) => {
                        const date = new Date(item.label);
                        if (item.label && item.label.split(',') && item.label.split(',').length === 2) {
                            const weeks = item.label.split(',');
                            let label = weeks.map((d: any) => getDay(d));

                            monthName = label.join(' - ');
                        } else if (item.label && item.label.split('-') && item.label.split('-').length === 3) {
                            monthName = date.toLocaleDateString(currentLanguage, {day: 'numeric', month: 'short'});
                        } else {
                            monthName = date.toLocaleDateString(currentLanguage, {month: 'long'});
                            monthName = monthName.charAt(0).toUpperCase() + monthName.slice(1);
                        }

                        return monthName;
                    });

                const totalRow: any = {
                    [$tableColumnLabel]: $totalCountriesLabel.innerText.trim(),
                };

                labels.forEach((label, index) => {
                    const totalMonth = totalData[index].value;

                    totalRow[label] = totalMonth.toLocaleString().replace(/,/g, '.') + ' ' + $tooltipLabel.innerText.trim();
                });

                const totalTableData = [totalRow];

                data.forEach(countryData => {
                    const rowData: any = {
                        [$tableColumnLabel]: Drupal.t(countryData.label),
                    };
                    countryData.period.forEach((periodItem: any) => {
                        const date = new Date(periodItem.label);
                        let monthName;
                        if (periodItem.label && periodItem.label.split(',') && periodItem.label.split(',').length === 2) {
                            const weeks = periodItem.label.split(',');
                            let label = weeks.map((d: any) => getDay(d));

                            monthName = label.join(' - ');
                        } else if (periodItem.label && periodItem.label.split('-') && periodItem.label.split('-').length === 3) {
                            monthName = date.toLocaleDateString(currentLanguage, {day: 'numeric', month: 'short'});
                        } else {
                            monthName = date.toLocaleDateString(currentLanguage, {month: 'long'});
                            monthName = monthName.charAt(0).toUpperCase() + monthName.slice(1);
                        }

                        rowData[monthName] = periodItem.value.toLocaleString().replace(/,/g, '.') + ' ' + $tooltipLabel.innerText.trim();
                    });

                    totalTableData.push(rowData);

                    totalTableData.sort((a, b) => {
                        const nameA = a?.Paese?.toUpperCase() || a?.Country?.toUpperCase();
                        const nameB = b?.Paese?.toUpperCase() || b?.Country?.toUpperCase();

                        if (
                            (a?.Paese === 'Tutti i paesi' && b?.Paese === 'Tutti i paesi') ||
                            (a?.Country === 'All countries' && b?.Country === 'All countries') ||
                            (a?.Country === 'Tutti i paesi' && b?.Country === 'Tutti i paesi')
                        )
                            return 0;
                        if (a?.Paese === 'Tutti i paesi' || a?.Country === 'All countries' || a?.Country === 'Tutti i paesi') return -1;
                        if (b?.Paese === 'Tutti i paesi' || b?.Country === 'All countries' || b?.Country === 'Tutti i paesi') return 1;

                        if (nameA < nameB) {
                            return -1;
                        }
                        if (nameA > nameB) {
                            return 1;
                        }
                        return 0;
                    });
                });

                let tabulator = new Tabulator($table, {
                    data: totalTableData,
                    layout: 'fitColumns',
                    columns: [
                        {
                            title: $tableColumnLabel,
                            field: $tableColumnLabel,
                            headerSort: false,
                            frozen: true,
                        },
                        ...labels.map(label => ({title: label, field: label, headerSort: false})),
                    ],
                    pagination: totalTableData.length > 30,
                    paginationSize: limitLength,
                    paginationButtonCount: 3,
                });

                tabulator.on('dataLoaded', function () {
                    const paginator = $table.querySelector('.tabulator-paginator') as HTMLElement;
                    const btnPrev = $table.querySelector('button[data-page="prev"]') as HTMLElement;
                    const btnNext = $table.querySelector('button[data-page="next"]') as HTMLElement;
                    if (btnPrev) {
                        btnPrev.innerHTML = '';
                    }
                    if (btnNext) {
                        btnNext.innerHTML = '';
                    }
                    paginator && paginator.setAttribute('aria-label', 'navigazione del paginatore');
                    paginator && paginator.setAttribute('role', 'navigation');
                });
            }

            function createLegend(data: any) {
                let legend = $chart.querySelector('.legend-container') as HTMLElement;
                legend.innerHTML = ``;

                let dataEl: any;

                if (chartType === 'doughnut') {
                    dataEl = approximateArray(data);
                } else {
                    dataEl = data;
                }

                dataEl.forEach((item: any, index: number) => {
                    const legendItem = document.createElement('div');
                    legendItem.classList.add('legend-item');
                    const tile = document.createElement('div');
                    tile.classList.add('tile');
                    tile.style.backgroundColor = colors[index];
                    const content = document.createElement('div');
                    content.classList.add('content');
                    const value = document.createElement('div');
                    value.classList.add('value');
                    value.textContent += item.value ? item.value : item.percentage ? Number(item.percentage) : 0;
                    const span = document.createElement('span');
                    span.textContent += '%';
                    const legendLabel = document.createElement('div');
                    legendLabel.classList.add('label');
                    legendLabel.textContent += item.label;

                    legendItem.appendChild(tile);
                    value.appendChild(span);
                    content.appendChild(value);
                    content.appendChild(legendLabel);
                    legendItem.appendChild(content);
                    legend.appendChild(legendItem);
                });
            }

            function bindChart(period, numberOfPeriod, country = '') {
                if (chartType) {
                    $chart.classList.add('has-spinner');
                }

                try {
                    switch (chartType) {
                        case 'line':
                            resolve
                                .run('getVisits', period, numberOfPeriod, (country && country !== '*' ? country : '') as any)
                                .then((res: any) => {
                                    if (!res.data) {
                                        throw new Error();
                                    } else {
                                        timestamp = res.timestamp;
                                        dataTable = res.data.countries;
                                        data = (country && country === '*') || !country ? res.data.totalPeriod : res.data.countries[0].period;

                                        bindYear(data);

                                        bindCountrySelect(res.data.filters.country, country);

                                        $tabContents.classList.remove('d-none');

                                        const $spanNumberValue = $number.querySelector('.d-none') as HTMLElement;
                                        const numberValue = $spanNumberValue && $spanNumberValue.innerText;
                                        const splitNumberValues = numberValue && numberValue.split(',');

                                        function nFormatter(num: any) {
                                            if (num >= 1000000000) {
                                                return (num / 1000000000).toFixed(3).replace(/\.0$/, '').replace('.', ',') + '<span class="value">' + splitNumberValues[2] + '</span>';
                                            }
                                            if (num >= 1000000) {
                                                return (num / 1000000).toFixed(3).replace(/\.0$/, '').replace('.', ',') + '<span class="value">' + splitNumberValues[1] + '</span>';
                                            }
                                            if (num >= 1000) {
                                                return (num / 1000).toFixed(3).replace(/\.0$/, '').replace('.', ',') + '<span class="value">' + splitNumberValues[0] + '</span>';
                                            }
                                            return num.toString().replace('.', ',');
                                        }

                                        $number.innerHTML = nFormatter((country && country === '*') || !country ? res.data.total : res.data.countries[0].total) + $initialNumberLabel;

                                        setTimeout(() => {
                                            create(data, index);
                                            createTable(dataTable, res.data.totalPeriod);
                                            $chart.classList.remove('has-spinner');
                                            $chart.querySelector('.chart-wrapper').classList.remove('vis-hidden');

                                            if (data && data.length) {
                                                $noResult?.classList.add('d-none');
                                                $tabContents.classList.remove('d-none');
                                            } else {
                                                $noResult?.classList.remove('d-none');
                                                $tabContents.classList.add('d-none');
                                            }
                                        }, SPINNER_DURATION);
                                    }
                                })
                                .catch(e => {
                                    //console.log(e);
                                    setTimeout(() => {
                                        const $year = $chart.querySelector('.year') as HTMLDivElement;
                                        const $lastUpdateDate = $chart.querySelector('.info .last-update') as HTMLDivElement;
                                        $number?.classList.add('d-none');
                                        $year?.classList.add('d-none');
                                        $noResult?.classList.remove('d-none');
                                        $tabContents.classList.add('d-none');
                                        ($lastUpdateDate?.parentElement as HTMLDivElement)?.classList.add('d-none');
                                        $chart.classList.remove('has-spinner');
                                        $chart.querySelector('.chart-wrapper')?.classList.remove('vis-hidden');
                                    }, SPINNER_DURATION);
                                });
                            break;

                        case 'doughnut':
                            if (chartApi === 'DatasetDoughnutChannels') {
                                resolve
                                    .run('getChannels', period, numberOfPeriod)
                                    .then((res: any) => {
                                        if (!res.data) {
                                            throw new Error();
                                        } else {
                                            timestamp = res.timestamp;
                                            data = [...res.data.channels];
                                            if (data.find(item => item.label === 'others')) {
                                                let othersLabelIndex = data.findIndex(item => item.label === 'others');
                                                if (!isNaN(othersLabelIndex) && othersLabelIndex >= 0) {
                                                    data[othersLabelIndex].label = $othersLabel.innerText;
                                                }
                                            }

                                            $tabContents.classList.remove('d-none');

                                            setTimeout(() => {
                                                if (data && data.length) {
                                                    create(data, index);
                                                    createLegend(data);
                                                    $noResult?.classList.add('d-none');
                                                    $tabContents.classList.remove('d-none');
                                                } else {
                                                    $noResult?.classList.remove('d-none');
                                                    $tabContents.classList.add('d-none');
                                                }

                                                $chart.classList.remove('has-spinner');
                                                $chart.querySelector('.chart-wrapper').classList.remove('vis-hidden');
                                            }, SPINNER_DURATION);
                                        }
                                    })
                                    .catch(e => {
                                        //console.log(e);
                                        setTimeout(() => {
                                            $noResult?.classList.remove('d-none');
                                            $tabContents.classList.add('d-none');
                                            $chart.classList.remove('has-spinner');
                                            $chart.querySelector('.chart-wrapper').classList.remove('vis-hidden');
                                        }, SPINNER_DURATION);
                                    });
                            } else if (chartApi === 'DatasetDoughnutTargets') {
                                resolve
                                    .run('getTargets', period, numberOfPeriod)
                                    .then((res: any) => {
                                        if (!res.data) {
                                            throw new Error();
                                        } else {
                                            timestamp = res.timestamp;
                                            data = [...res.data.list];
                                            if (data.find(item => item.label === 'others')) {
                                                let othersLabelIndex = data.findIndex(item => item.label === 'others');
                                                if (!isNaN(othersLabelIndex) && othersLabelIndex >= 0) {
                                                    data[othersLabelIndex].label = $othersLabel.innerText;
                                                }
                                            }

                                            $tabContents.classList.remove('d-none');

                                            setTimeout(() => {
                                                if (data && data.length) {
                                                    create(data, index);
                                                    createLegend(data);
                                                    $noResult?.classList.add('d-none');
                                                    $tabContents.classList.remove('d-none');
                                                } else {
                                                    $noResult?.classList.remove('d-none');
                                                    $tabContents.classList.add('d-none');
                                                }

                                                $chart.classList.remove('has-spinner');
                                                $chart.querySelector('.chart-wrapper').classList.remove('vis-hidden');
                                            }, SPINNER_DURATION);
                                        }
                                    })
                                    .catch(e => {
                                        //console.log(e);
                                        setTimeout(() => {
                                            $noResult?.classList.remove('d-none');
                                            $tabContents.classList.add('d-none');
                                            $chart.classList.remove('has-spinner');
                                            $chart.querySelector('.chart-wrapper').classList.remove('vis-hidden');
                                        }, SPINNER_DURATION);
                                    });
                            }
                            break;
                    }
                } catch (e) {
                    console.log(e);
                    $chart.querySelector('.chart-wrapper').classList.remove('vis-hidden');
                    $chart.classList.remove('has-spinner');
                    ($chart.querySelector('.chart-table-wrapper') as HTMLElement).classList.add('d-none');
                }
            }

            function bindSelect() {
                $filtersSelect.forEach(($select: any) => {
                    const $options = $select.querySelectorAll('.option-div');
                    const selectType = $select.getAttribute('data-select-type');

                    $options.forEach(($option: any) => {
                        $option.onclick = () => {
                            if (selectType === 'period') {
                                let numberOfPeriod = $option.getAttribute('option-id');
                                numberOfPeriod = numberOfPeriod && numberOfPeriod.split('-') ? numberOfPeriod.split('-')[0] : null;
                                const period = $option.getAttribute('data-type');
                                initialDefaultPeriod = period ? period : initialDefaultPeriod;
                                initialDefaultNumberOfPeriod = numberOfPeriod ? numberOfPeriod : initialDefaultNumberOfPeriod;
                            } else {
                                const country = $option.getAttribute('option-id');
                                initialDefaultCountry = country ? country : initialDefaultCountry;
                            }

                            bindChart(initialDefaultPeriod, initialDefaultNumberOfPeriod, initialDefaultCountry);
                        };
                    });
                });
            }

            function bindInitialSelect() {
                const filters = PERIOD_FILTERS || [];

                $filtersSelect.forEach(($select: any) => {
                    if ($select.getAttribute('data-select-type') && $select.getAttribute('data-select-type') === 'period') {
                        const $nativeSelect = $select.querySelector('select');
                        filters.forEach((filterEL: any) => {
                            const $option = document.createElement('option') as HTMLOptionElement;
                            $option.setAttribute('value', filterEL.n_period + '-' + filterEL.period);
                            $option.setAttribute('data-type', filterEL.period);
                            $option.innerText = Drupal.t(filterEL.label);
                            $nativeSelect.appendChild($option);
                        });

                        SearchableSelect.init($nativeSelect, 'data-view-select');
                    }
                });

                $filtersSelect = $chart.querySelectorAll('.select-wrapper .select-container');

                bindSelect();
            }

            function bindCountrySelect(countries, countrySelected) {
                $filtersSelect.forEach(($select: any) => {
                    if ($select.getAttribute('data-select-type') && $select.getAttribute('data-select-type') === 'country') {
                        const $nativeSelect = $select.querySelector('select');
                        const $initialOptions = $nativeSelect.querySelectorAll('option:not(.default)');
                        $initialOptions.forEach(($option: any) => $option.remove());

                        countries.forEach((country: any) => {
                            const $option = document.createElement('option') as HTMLOptionElement;
                            $option.setAttribute('value', country.code);
                            $option.innerText = Drupal.t(country.label);
                            $nativeSelect.appendChild($option);

                            const $options = Array.from($nativeSelect.querySelectorAll('option'));
                            $options.sort(function (a, b) {
                                if (a.value === '*' && b.value === '*') return 0;
                                if (a.value === '*') return -1;
                                if (b.value === '*') return 1;

                                if (a.textContent.toUpperCase() > b.textContent.toUpperCase()) return 1;
                                else if (a.textContent.toUpperCase() < b.textContent.toUpperCase()) return -1;
                                else return 0;
                            });

                            $nativeSelect.innerHTML = '';

                            $options.forEach(option => {
                                $nativeSelect.appendChild(option);
                            });

                            $option.selected = countrySelected && countrySelected === country.code;
                        });

                        SearchableSelect.init($nativeSelect, 'country-select');

                        $filtersSelect = $chart.querySelectorAll('.select-wrapper .select-container');

                        bindSelect();
                    }
                });
            }

            function bindYear(data) {
                const $year = $chart.querySelector('.year') as HTMLDivElement;
                $year.innerText = '';
                if (data && data.length) {
                    const dataLength = data.length;
                    let firstYear = null as any;
                    let lastYear = null as any;

                    if (dataLength > 1) {
                        firstYear = data[0].label ? data[0].label.substring(0, 4) : '';
                        const lastSplitLength = (data[dataLength - 1].label && data[dataLength - 1].label.split(',') && data[dataLength - 1].label.split(',').length) || null;
                        lastYear =
                            lastSplitLength && data[dataLength - 1].label.split(',')[lastSplitLength - 1] && data[dataLength - 1].label.split(',')[lastSplitLength - 1].substring(0, 4)
                                ? data[dataLength - 1].label.split(',')[lastSplitLength - 1].substring(0, 4)
                                : '';
                    } else {
                        firstYear = data[0].label ? data[0].label.substring(0, 4) : '';
                        lastYear = data[0].label ? data[0].label.substring(0, 4) : '';
                    }

                    if (firstYear === lastYear) {
                        $year.innerText = initialYearText + ' ' + firstYear;
                    } else {
                        $year.innerText = initialYearText + ' ' + firstYear + '-' + lastYear;
                    }
                }
            }

            window.addEventListener('orientationchange', () => {
                $chart.querySelector('.chart-wrapper').classList.add('vis-hidden');
                bindChart(initialDefaultPeriod, initialDefaultNumberOfPeriod, initialDefaultCountry);
            });
        });
    }
}
